import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sellersApi, type Seller, ApiError } from '../lib/api';

// Flowwow shape: { shopId, name, status, address, currency, isVerified, workingDays, _bucket }
// `_bucket` is the bucket we probed (active/disabled/moderation) so we can
// display it as a hint in the UI.
type RawShop = {
  shopId?: number | string;
  id?: number | string;
  name?: string;
  title?: string;
  status?: string;
  address?: string;
  isVerified?: boolean;
  _bucket?: string;
  [k: string]: unknown;
};

function extractShops(payload: unknown): RawShop[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as RawShop[];
  if (typeof payload === 'object') {
    const r = payload as Record<string, unknown>;
    // New shape: { ok, shops: [...], raw, errors }
    if (Array.isArray(r.shops)) return r.shops as RawShop[];
    // Fallback shapes
    for (const key of ['data', 'items', 'list', 'result']) {
      const v = r[key];
      if (Array.isArray(v)) return v as RawShop[];
    }
    // Or a single nested raw response.
    if (r.raw && typeof r.raw === 'object' && Array.isArray((r.raw as Record<string, unknown>).shops)) {
      return (r.raw as { shops: RawShop[] }).shops;
    }
  }
  return [];
}

export default function SellerEdit() {
  const { id: idStr } = useParams();
  const id = Number(idStr);
  const nav = useNavigate();

  const [seller, setSeller] = useState<Seller | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [shopId, setShopId] = useState<string>('');
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Token-check state
  const [checking, setChecking] = useState(false);
  const [checkErr, setCheckErr] = useState<string | null>(null);
  const [shops, setShops] = useState<RawShop[] | null>(null);
  const [shopsRaw, setShopsRaw] = useState<unknown>(null);

  // Password rotation
  const [newPwd, setNewPwd] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState<string | null>(null);

  useEffect(() => {
    sellersApi.get(id)
      .then((r) => {
        setSeller(r.seller);
        setDisplayName(r.seller.display_name ?? '');
        setEmail(r.seller.contact_email ?? '');
        setShopId(r.seller.flowwow_shop_id?.toString() ?? '');
      })
      .catch((e) => setErr(String(e?.message ?? e)));
  }, [id]);

  if (err) return <div className="container"><div className="error">{err}</div></div>;
  if (!seller) return <div className="container"><p className="muted">Загрузка…</p></div>;

  async function save() {
    setSavedMsg(null);
    try {
      const patch: Parameters<typeof sellersApi.update>[1] = {
        display_name: displayName.trim() || null,
        contact_email: email.trim() || null,
        flowwow_shop_id: shopId ? Number(shopId) : null,
      };
      // Send token only if user actually typed something (avoid wiping it
      // with an empty field). Explicit clearing is a future feature.
      if (token.trim()) patch.flowwow_token = token.trim();

      const r = await sellersApi.update(id, patch);
      setSeller(r.seller);
      setToken('');
      setSavedMsg('Сохранено');
    } catch (e) {
      setSavedMsg(null);
      setErr(String((e as Error)?.message ?? e));
    }
  }

  async function checkToken() {
    setChecking(true);
    setCheckErr(null);
    setShops(null);
    setShopsRaw(null);
    try {
      // If user typed a new token, save it first so /shops uses the fresh value.
      if (token.trim()) {
        await sellersApi.update(id, { flowwow_token: token.trim() });
        setToken('');
        const fresh = await sellersApi.get(id);
        setSeller(fresh.seller);
      }
      const r = await sellersApi.shops(id);
      setShopsRaw(r);
      setShops(extractShops(r));
    } catch (e) {
      if (e instanceof ApiError) {
        const body = (e.body ?? {}) as Record<string, unknown>;
        if (body.error === 'no_flowwow_token') {
          setCheckErr('Сначала введи и сохрани токен.');
        } else if (e.status === 401 || body.error === 'flowwow_unauthorized') {
          setCheckErr('Flowwow вернул 401 — токен невалиден.');
        } else if (body.error === 'flowwow_error') {
          // Show the original Flowwow error so the operator can react.
          const fw = body.flowwow_body as
            | { message?: string; fields?: Array<{ path: string; message: string }> }
            | undefined;
          const fields = fw?.fields?.map((f) => `${f.path}: ${f.message}`).join('; ');
          setCheckErr(`Flowwow ${body.flowwow_status}: ${fw?.message ?? e.message}${fields ? ` (${fields})` : ''}`);
        } else {
          setCheckErr(`Ошибка ${e.status}: ${e.message}`);
        }
      } else {
        setCheckErr(String((e as Error)?.message ?? e));
      }
    } finally {
      setChecking(false);
    }
  }

  async function rotatePassword() {
    if (newPwd.length < 8) { setPwdMsg('Мин. 8 символов'); return; }
    setPwdBusy(true);
    setPwdMsg(null);
    try {
      await sellersApi.password(id, newPwd);
      setNewPwd('');
      setPwdMsg('Пароль обновлён');
    } catch (e) {
      setPwdMsg(String((e as Error)?.message ?? e));
    } finally {
      setPwdBusy(false);
    }
  }

  async function toggleActive() {
    if (seller!.is_active) {
      if (!confirm(`Деактивировать продавца ${seller!.login}?`)) return;
      await sellersApi.deactivate(id);
    } else {
      await sellersApi.activate(id);
    }
    const fresh = await sellersApi.get(id);
    setSeller(fresh.seller);
  }

  return (
    <div className="container">
      <div className="page-head">
        <h2>Продавец: {seller.login}</h2>
        <div className="row">
          <Link to="/sellers" className="btn">← К списку</Link>
          <button className="btn" onClick={toggleActive}>
            {seller.is_active ? 'Деактивировать' : 'Активировать'}
          </button>
        </div>
      </div>

      <div className="card form">
        <h3>Профиль</h3>
        <label>
          <span>Имя для отображения</span>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label>
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>

        <h3 style={{ marginTop: 18 }}>Flowwow</h3>
        <p className="muted">
          Текущий токен: {seller.has_flowwow_token
            ? <code>{seller.flowwow_token_mask}</code>
            : <span>не задан</span>}
        </p>
        <label>
          <span>Новый Bearer-токен (оставь пустым, чтобы не менять)</span>
          <textarea value={token} onChange={(e) => setToken(e.target.value)} rows={3} />
        </label>

        <label>
          <span>flowwow_shop_id</span>
          <input
            type="number"
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            placeholder="Например 123456"
          />
        </label>

        <div className="row">
          <button className="btn primary" onClick={save}>Сохранить</button>
          <button className="btn" onClick={checkToken} disabled={checking}>
            {checking ? 'Проверка…' : 'Проверить токен (POST /apiseller/shops)'}
          </button>
          {savedMsg && <span className="ok-msg">{savedMsg}</span>}
        </div>

        {checkErr && <div className="error" style={{ marginTop: 12 }}>{checkErr}</div>}

        {shops && (
          <div className="card sub" style={{ marginTop: 12 }}>
            <h4>Магазины из Flowwow ({shops.length})</h4>
            {shops.length === 0 ? (
              <>
                <p className="muted">Список пуст. Возможно структура ответа другая — см. raw ниже.</p>
                <pre className="raw">{JSON.stringify(shopsRaw, null, 2)}</pre>
              </>
            ) : (
              <ul className="shop-picker">
                {shops.map((s, i) => {
                  const sid = Number(s.shopId ?? s.id);
                  const label = s.name?.trim() || s.title || `#${sid}`;
                  const verified = s.isVerified ? ' ✓' : '';
                  return (
                    <li key={i}>
                      <button
                        className={`btn ${shopId === String(sid) ? 'primary' : ''}`}
                        onClick={() => setShopId(String(sid))}
                        type="button"
                        title={[s.address, s._bucket && `bucket: ${s._bucket}`, s.status && `status: ${s.status}`].filter(Boolean).join('\n')}
                      >
                        {label}{verified}
                        <span className="muted"> (id={sid}{s._bucket ? `, ${s._bucket}` : ''})</span>
                        {s.address && <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.7 }}>{s.address}</div>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="card form" style={{ marginTop: 20 }}>
        <h3>Сбросить пароль</h3>
        <div className="row">
          <input
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            placeholder="Новый пароль (мин. 8)"
            minLength={8}
          />
          <button className="btn" onClick={rotatePassword} disabled={pwdBusy}>
            {pwdBusy ? 'Сохраняем…' : 'Сбросить'}
          </button>
          {pwdMsg && <span className="ok-msg">{pwdMsg}</span>}
        </div>
      </div>
    </div>
  );
}
